// px of accumulated scroll delta to fully open or close the drawer
const OPEN_DISTANCE = 300;
// px of deliberate overscroll before the drawer begins to appear
const ACTIVATION_BUFFER = 150;
// ms of stillness at the page bottom before the intercept arms
const SETTLE_DELAY = 600;

function normalizeWheelDelta(e) {
  if (e.deltaMode === 1) return e.deltaY * 20;
  if (e.deltaMode === 2) return e.deltaY * window.innerHeight;
  return e.deltaY;
}

class ArchiveDrawer extends HTMLElement {
  constructor() {
    super();
    this._progress = 0;
    this._bufferAccum = 0;
    this._injected = false;
    this._isOpen = false;
    this._isIntercepting = false;
    this._isLocked = false;
    this._settling = false;
    this._settleTimer = null;
    this._touchStartY = 0;

    this._onPageScroll = this._onPageScroll.bind(this);
    this._onWheelPage = this._onWheelPage.bind(this);
    this._onWheelContent = this._onWheelContent.bind(this);
    this._onTouchStartTrack = this._onTouchStartTrack.bind(this);
    this._onTouchMovePage = this._onTouchMovePage.bind(this);
    this._onTouchMoveContent = this._onTouchMoveContent.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onFocusout = this._onFocusout.bind(this);
    this._onFocusin = this._onFocusin.bind(this);
    this._onArchiveLinkClick = this._onArchiveLinkClick.bind(this);
  }

  connectedCallback() {
    this._panel = this.querySelector(".archive-drawer__panel");

    // Track touch start position globally — passive, no overhead
    window.addEventListener("touchstart", this._onTouchStartTrack, {
      passive: true,
    });
    window.addEventListener("scroll", this._onPageScroll, { passive: true });
    this._panel.addEventListener("focusin", this._onFocusin);
    document.addEventListener("click", this._onArchiveLinkClick);

    // Check immediately in case the page is already scrolled to the bottom
    requestAnimationFrame(() => this._onPageScroll());
  }

  disconnectedCallback() {
    clearTimeout(this._settleTimer);
    window.removeEventListener("touchstart", this._onTouchStartTrack);
    window.removeEventListener("scroll", this._onPageScroll);
    window.removeEventListener("wheel", this._onWheelPage);
    window.removeEventListener("touchmove", this._onTouchMovePage);
    document.removeEventListener("keydown", this._onKeydown);
    this._panel?.removeEventListener("focusin", this._onFocusin);
    document.removeEventListener("click", this._onArchiveLinkClick);
    this._panel?.removeEventListener("focusout", this._onFocusout);
    this._content?.removeEventListener("wheel", this._onWheelContent);
    this._content?.removeEventListener("touchmove", this._onTouchMoveContent);
    if (this._isLocked) window.unlockScroll(this);
  }

  _isAtPageBottom() {
    return (
      window.scrollY + window.innerHeight >=
      document.documentElement.scrollHeight - 5
    );
  }

  _onPageScroll() {
    if (this._isLocked || this._isOpen) return;

    // Pre-inject content when within 2 viewports of the bottom
    if (
      !this._injected &&
      document.documentElement.scrollHeight -
        window.scrollY -
        window.innerHeight <
        window.innerHeight * 2
    ) {
      this._inject();
    }

    if (this._isAtPageBottom()) {
      if (!this._settling) {
        this._settling = true;
        this._settleTimer = setTimeout(() => {
          this._settling = false;
          this._beginIntercept();
        }, SETTLE_DELAY);
      }
    } else if (this._settling) {
      // User scrolled away from the bottom before the timer fired — cancel
      clearTimeout(this._settleTimer);
      this._settling = false;
    }
  }

  _beginIntercept() {
    if (this._isIntercepting) return;
    this._isIntercepting = true;

    if (!this._injected) this._inject();

    window.removeEventListener("scroll", this._onPageScroll);

    // Don't lock scroll here — only lock once the drawer actually starts moving
    window.addEventListener("wheel", this._onWheelPage, { passive: false });
    window.addEventListener("touchmove", this._onTouchMovePage, {
      passive: false,
    });
  }

  _endIntercept() {
    if (!this._isIntercepting) return;
    this._isIntercepting = false;

    window.removeEventListener("wheel", this._onWheelPage);
    window.removeEventListener("touchmove", this._onTouchMovePage);

    if (this._isLocked) {
      window.unlockScroll(this);
      this._isLocked = false;
    }

    window.addEventListener("scroll", this._onPageScroll, { passive: true });
  }

  _inject() {
    if (this._injected) return;
    this._injected = true;

    const template = this.querySelector("[data-archive-template]");
    if (!template) return;

    this._panel.appendChild(template.content.cloneNode(true));
    this._content = this._panel.querySelector(".archive-drawer__content");

    this._panel
      .querySelector("[data-archive-close]")
      ?.addEventListener("click", () => this._close());
  }

  _onWheelPage(e) {
    const delta = normalizeWheelDelta(e);
    // Upward scroll before the drawer has opened — cancel and let page scroll
    if (delta < 0 && this._progress <= 0) {
      this._endIntercept();
      return;
    }
    e.preventDefault();
    this._drive(delta);
  }

  _onTouchStartTrack(e) {
    this._touchStartY = e.touches[0].clientY;
  }

  _onTouchMovePage(e) {
    const dy = (this._touchStartY - e.touches[0].clientY) * 0.4;
    this._touchStartY = e.touches[0].clientY;
    // Upward swipe before the drawer has opened — cancel and let page scroll
    if (dy < 0 && this._progress <= 0) {
      this._endIntercept();
      return;
    }
    e.preventDefault();
    this._drive(dy);
  }

  _onWheelContent(e) {
    const delta = normalizeWheelDelta(e);
    if (delta < 0 && (this._content?.scrollTop ?? 0) <= 0) {
      e.preventDefault();
      this._beginContentClose(delta);
    }
  }

  _onTouchMoveContent(e) {
    const dy = (this._touchStartY - e.touches[0].clientY) * 0.4;
    this._touchStartY = e.touches[0].clientY;
    if (dy < 0 && (this._content?.scrollTop ?? 0) <= 0) {
      e.preventDefault();
      this._beginContentClose(dy);
    }
  }

  _beginContentClose(initialDelta) {
    this._isOpen = false;
    this._panel.classList.remove("archive-drawer__panel--open");
    if (this._content) {
      this._content.style.overflowY = "hidden";
      this._content.removeEventListener("wheel", this._onWheelContent);
      this._content.removeEventListener("touchmove", this._onTouchMoveContent);
    }
    document.removeEventListener("keydown", this._onKeydown);
    this._panel.removeEventListener("focusout", this._onFocusout);

    // Hand off to page-level interception for the close animation
    this._isIntercepting = true;
    window.addEventListener("wheel", this._onWheelPage, { passive: false });
    window.addEventListener("touchmove", this._onTouchMovePage, {
      passive: false,
    });

    this._drive(initialDelta);
  }

  _drive(delta) {
    // Require a deliberate overscroll before the drawer starts to appear
    if (this._progress <= 0 && delta > 0) {
      this._bufferAccum += delta;
      if (this._bufferAccum < ACTIVATION_BUFFER) return;
      // Carry excess past the buffer into the animation
      delta = this._bufferAccum - ACTIVATION_BUFFER;
    }

    const newProgress = Math.max(
      0,
      Math.min(1, this._progress + delta / OPEN_DISTANCE),
    );
    if (newProgress === this._progress) return;

    // Lock scroll the first time the drawer actually starts moving
    if (newProgress > 0 && !this._isLocked) {
      window.lockScroll(this);
      this._isLocked = true;
    }

    this._progress = newProgress;

    this._panel.style.transform = `translateY(${(1 - this._progress) * 100}%)`;
    this._panel.style.pointerEvents = this._progress > 0 ? "auto" : "none";

    if (this._progress >= 1) {
      this._fullyOpen();
    } else if (this._progress <= 0) {
      this._fullyClosed();
    }
  }

  _fullyOpen() {
    if (this._isOpen) return;
    this._isOpen = true;
    this._progress = 1;

    this._panel.style.transform = "translateY(0%)";
    this._panel.style.pointerEvents = "auto";
    this._panel.classList.add("archive-drawer__panel--open");
    if (this._content) this._content.style.overflowY = "auto";

    // Stop page interception; let the archive content handle its own scroll
    this._isIntercepting = false;
    window.removeEventListener("wheel", this._onWheelPage);
    window.removeEventListener("touchmove", this._onTouchMovePage);

    // Add close interceptors on the content element
    if (this._content) {
      this._content.addEventListener("wheel", this._onWheelContent, {
        passive: false,
      });
      this._content.addEventListener("touchmove", this._onTouchMoveContent, {
        passive: false,
      });
    }

    this._lastFocused = document.activeElement;
    this._panel.focus({ preventScroll: true });
    document.addEventListener("keydown", this._onKeydown);
    this._panel.addEventListener("focusout", this._onFocusout);
  }

  _fullyClosed() {
    this._progress = 0;
    this._bufferAccum = 0;
    this._panel.style.transform = "translateY(100%)";
    this._panel.style.pointerEvents = "none";
    this._panel.classList.remove("archive-drawer__panel--open");
    this._lastFocused?.focus({ preventScroll: true });

    this._endIntercept();
  }

  _openAnimated(lastFocused) {
    if (!this._injected) this._inject();
    if (!this._isLocked) {
      window.lockScroll(this);
      this._isLocked = true;
    }

    this._lastFocused = lastFocused;
    this._isOpen = true;
    this._progress = 1;

    this._panel.style.pointerEvents = "auto";
    this._panel.classList.add("archive-drawer__panel--open");
    if (this._content) this._content.style.overflowY = "auto";

    this._panel.style.transition =
      "transform 500ms cubic-bezier(0.33, 1, 0.68, 1)";
    this._panel.style.transform = "translateY(0%)";
    this._panel.addEventListener(
      "transitionend",
      () => { this._panel.style.transition = ""; },
      { once: true },
    );

    this._panel.focus({ preventScroll: true });
    document.addEventListener("keydown", this._onKeydown);
    this._panel.addEventListener("focusout", this._onFocusout);
    if (this._content) {
      this._content.addEventListener("wheel", this._onWheelContent, {
        passive: false,
      });
      this._content.addEventListener("touchmove", this._onTouchMoveContent, {
        passive: false,
      });
    }
  }

  _onArchiveLinkClick(e) {
    const link = e.target.closest('a[href="#archive"]');
    if (!link) return;
    e.preventDefault();
    if (this._isOpen) return;

    clearTimeout(this._settleTimer);
    this._settling = false;
    if (this._isIntercepting) {
      this._isIntercepting = false;
      window.removeEventListener("wheel", this._onWheelPage);
      window.removeEventListener("touchmove", this._onTouchMovePage);
    }

    this._openAnimated(document.activeElement);
  }

  _onFocusin(e) {
    if (this._isOpen || this._progress > 0 || this._isIntercepting) return;
    // e.relatedTarget is the element that had focus before entering the drawer
    this._openAnimated(e.relatedTarget);
  }

  _onKeydown(e) {
    if (e.key === "Escape") this._close();
  }

  _onFocusout() {
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (document.querySelector("markup-modal[open]")) return;
      if (active?.closest("markup-modal")) return;
      if (active && active !== document.body && this._panel.contains(active))
        return;
      this._close();
    });
  }

  _close() {
    if (!this._isOpen && this._progress === 0) return;

    const wasOpen = this._isOpen;
    this._isOpen = false;

    document.removeEventListener("keydown", this._onKeydown);
    this._panel.removeEventListener("focusout", this._onFocusout);
    this._panel.classList.remove("archive-drawer__panel--open");

    if (wasOpen && this._content) {
      this._content.removeEventListener("wheel", this._onWheelContent);
      this._content.removeEventListener("touchmove", this._onTouchMoveContent);
      this._content.style.overflowY = "hidden";
    }

    // Remove any active page-level interception
    this._isIntercepting = false;
    window.removeEventListener("wheel", this._onWheelPage);
    window.removeEventListener("touchmove", this._onTouchMovePage);

    this._panel.style.transition =
      "transform 500ms cubic-bezier(0.32, 0, 0.67, 0)";
    this._panel.style.transform = "translateY(100%)";
    this._panel.style.pointerEvents = "none";
    this._lastFocused?.focus({ preventScroll: true });

    this._panel.addEventListener(
      "transitionend",
      () => {
        this._panel.style.transition = "";
        this._progress = 0;
        this._bufferAccum = 0;
        if (this._content) this._content.scrollTop = 0;

        if (this._isLocked) {
          window.unlockScroll(this);
          this._isLocked = false;
        }
        window.addEventListener("scroll", this._onPageScroll, {
          passive: true,
        });
      },
      { once: true },
    );
  }
}

if (!customElements.get("archive-drawer")) {
  customElements.define("archive-drawer", ArchiveDrawer);
}
